import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  ArrowLeft,
  CircleAlert,
  CircleCheck,
  ClipboardPaste,
  FileDown,
  FileJson,
  FileUp,
  ShieldAlert,
  TriangleAlert,
  Undo2,
} from "lucide-react";
import { Modal } from "../components";
import {
  describeValidationError,
  friendlyMessage,
  parseJson,
  type JsonSyntaxProblem,
  type ValidationProblem,
} from "./diagnostics";
import {
  describeSummary,
  summarizeChanges,
  type ChangeSummary,
} from "./changes";
import { JsonCodeEditor, JsonProblem } from "./JsonCodeEditor";

/** What the importer found, shown on the Review step. */
export interface ImportPlan {
  /** Headline for the review, e.g. "Business card · 2 pages". */
  title?: string;
  /** Facts about the file, shown as a definition list. */
  summary?: { label: string; value: ReactNode }[];
  /** Current and incoming values; the review lists the differences. */
  before?: unknown;
  after?: unknown;
  /** Pre-computed differences (otherwise derived from before/after). */
  changes?: ChangeSummary;
  /** Things worth knowing that don't block the import. */
  warnings?: string[];
  /** Describe what gets overwritten; the person must tick a box to continue. */
  replaces?: string;
  /** Button text, e.g. "Restore customization". */
  confirmLabel?: string;
}

export interface JsonImportSource {
  /** File name, or undefined when pasted. */
  name?: string;
  size: number;
}

export interface JsonImportOptions {
  /** Dialog title, e.g. "Restore customization". */
  title: string;
  /** One or two sentences: what this file is and where people get it. */
  description?: ReactNode;
  /** Validate and describe the parsed JSON. Throw to report problems. */
  inspect: (
    value: unknown,
    source: JsonImportSource,
  ) => ImportPlan | Promise<ImportPlan>;
  /** Commit the import. Return a success message, or throw to report a failure. */
  onConfirm: (
    value: unknown,
    plan: ImportPlan,
    source: JsonImportSource,
  ) => void | string | Promise<void | string>;
  /** Skip the Review step when the host already reviews (it opens a draft, say). */
  review?: boolean;
  /** Offered as "Download an example". */
  sample?: { fileName: string; value: unknown };
  /** Largest accepted file in bytes (default 25 MB). */
  maxBytes?: number;
  /** Offered after success, e.g. to restore the previous state. */
  onUndo?: () => void | Promise<void>;
  /** "close" skips the final summary screen (the host shows the result). */
  finish?: "summary" | "close";
}

type Step = "choose" | "review" | "done";
const MB = 1024 * 1024;
const formatBytes = (n: number) =>
  n >= MB
    ? `${(n / MB).toFixed(1)} MB`
    : n >= 1024
      ? `${Math.round(n / 1024)} KB`
      : `${n} bytes`;

function download(fileName: string, value: unknown) {
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(value, null, 2)], { type: "application/json" }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function Steps({ step }: { step: Step }) {
  const steps: [Step, string][] = [
    ["choose", "Choose file"],
    ["review", "Review"],
    ["done", "Done"],
  ];
  const at = steps.findIndex(([s]) => s === step);
  return (
    <ol className="ps-stepper" aria-label="Import steps">
      {steps.map(([key, label], i) => (
        <li
          key={key}
          aria-current={i === at ? "step" : undefined}
          data-state={i < at ? "complete" : i === at ? "current" : "upcoming"}
        >
          <span className="ps-stepper-dot">
            {i < at ? <CircleCheck size={14} /> : i + 1}
          </span>
          {label}
        </li>
      ))}
    </ol>
  );
}

export function ValidationList({
  problems,
}: {
  problems: ValidationProblem[];
}) {
  return (
    <ul className="ps-problem-list">
      {problems.map((problem, i) => (
        <li key={i}>
          {problem.where && <strong>{problem.where}</strong>}
          <span>{problem.message}</span>
        </li>
      ))}
    </ul>
  );
}

export function ChangeList({ summary }: { summary: ChangeSummary }) {
  if (!summary.total)
    return <p className="ps-muted">The file matches what you have now.</p>;
  return (
    <div className="ps-change-list">
      <p className="ps-change-counts">
        {summary.added > 0 && (
          <span data-kind="added">{summary.added} added</span>
        )}
        {summary.changed > 0 && (
          <span data-kind="changed">{summary.changed} changed</span>
        )}
        {summary.removed > 0 && (
          <span data-kind="removed">{summary.removed} removed</span>
        )}
      </p>
      <ul aria-label={describeSummary(summary)}>
        {summary.items.map((item, i) => (
          <li key={i} data-kind={item.kind}>
            <span className="ps-change-kind">{item.kind}</span>
            <span className="ps-change-where">{item.where}</span>
            <span className="ps-change-values">
              {item.kind === "changed" ? (
                <>
                  <del>{item.before}</del> → <ins>{item.after}</ins>
                </>
              ) : item.kind === "added" ? (
                <ins>{item.after}</ins>
              ) : (
                <del>{item.before}</del>
              )}
            </span>
          </li>
        ))}
      </ul>
      {summary.truncated && (
        <p className="ps-muted">
          …and {summary.total - summary.items.length} more.
        </p>
      )}
    </div>
  );
}

/**
 * The guided JSON importer: drop or paste a file, see problems explained in
 * plain words with the exact line, fix them in place, review what will
 * change, then confirm. Nothing is changed before the final button.
 */
export function JsonImportDialog({
  title,
  description,
  inspect,
  onConfirm,
  review = true,
  sample,
  maxBytes = 25 * MB,
  onUndo,
  finish = "summary",
  onClose,
  initialFile,
}: JsonImportOptions & { onClose: () => void; initialFile?: File }) {
  const id = useId(),
    fileInput = useRef<HTMLInputElement>(null),
    [step, setStep] = useState<Step>("choose"),
    [tab, setTab] = useState<"file" | "paste">("file"),
    [text, setText] = useState(""),
    [source, setSource] = useState<JsonImportSource>(),
    [syntax, setSyntax] = useState<JsonSyntaxProblem>(),
    [problems, setProblems] = useState<ValidationProblem[]>(),
    [plan, setPlan] = useState<ImportPlan>(),
    [value, setValue] = useState<unknown>(),
    [acknowledged, setAcknowledged] = useState(false),
    [busy, setBusy] = useState(false),
    [over, setOver] = useState(false),
    [failure, setFailure] = useState(""),
    [done, setDone] = useState(""),
    [undone, setUndone] = useState(false);

  const check = useCallback(
    async (raw: string, from: JsonImportSource) => {
      setSource(from);
      setText(raw);
      setSyntax(undefined);
      setProblems(undefined);
      setFailure("");
      setAcknowledged(false);
      const parsed = parseJson(raw);
      if (!parsed.ok) {
        setSyntax(parsed.problem);
        return;
      }
      setBusy(true);
      try {
        const found = await inspect(parsed.value, from);
        const changes =
          found.changes ??
          (found.before !== undefined || found.after !== undefined
            ? summarizeChanges(found.before, found.after ?? parsed.value)
            : undefined);
        const next = { ...found, changes };
        setValue(parsed.value);
        setPlan(next);
        if (!review) {
          await onConfirm(parsed.value, next, from);
          onClose();
          return;
        }
        setStep("review");
      } catch (error) {
        setProblems(describeValidationError(error));
      } finally {
        setBusy(false);
      }
    },
    [inspect, onConfirm, onClose, review],
  );

  const readFile = useCallback(
    async (file: File) => {
      setTab("file");
      if (file.size > maxBytes) {
        setSource({ name: file.name, size: file.size });
        setSyntax(undefined);
        setProblems([
          {
            where: "",
            path: [],
            message: `${file.name} is ${formatBytes(file.size)}. The largest file this importer accepts is ${formatBytes(maxBytes)}.`,
          },
        ]);
        return;
      }
      const looksBinary =
        /\.(zip|pdf|png|jpe?g|webp|tiff?|idml|indd|xlsx?|csv|docx)$/i.test(
          file.name,
        );
      if (looksBinary) {
        setSource({ name: file.name, size: file.size });
        setSyntax(undefined);
        setProblems([
          {
            where: "",
            path: [],
            message: `${file.name} isn't a JSON file. Choose the .json file instead.`,
          },
        ]);
        return;
      }
      await check(await file.text(), { name: file.name, size: file.size });
    },
    [check, maxBytes],
  );

  useEffect(() => {
    if (initialFile) void readFile(initialFile);
    // Only the file the dialog opened with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fix = () => {
    setTab("paste");
    setSyntax(undefined);
    setProblems(undefined);
  };

  const confirm = async () => {
    if (!plan || !source) return;
    setBusy(true);
    setFailure("");
    try {
      const message = await onConfirm(value, plan, source);
      if (finish === "close") {
        onClose();
        return;
      }
      setDone(
        typeof message === "string" && message ? message : "Import complete.",
      );
      setStep("done");
    } catch (error) {
      setFailure(friendlyMessage(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={title} onClose={onClose} wide>
      <div className="ps-import">
        <Steps step={step} />
        {step === "choose" && (
          <>
            {description && (
              <div className="ps-import-intro">{description}</div>
            )}
            <div
              className="ps-segmented"
              role="tablist"
              aria-label="How to add the JSON"
            >
              <button
                type="button"
                role="tab"
                aria-selected={tab === "file"}
                onClick={() => setTab("file")}
              >
                <FileUp size={14} aria-hidden /> Upload a file
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === "paste"}
                onClick={() => setTab("paste")}
              >
                <ClipboardPaste size={14} aria-hidden /> Paste or edit JSON
              </button>
            </div>
            {tab === "file" ? (
              <label
                className="ps-dropzone"
                data-over={over ? "" : undefined}
                onDragOver={(e) => {
                  e.preventDefault();
                  setOver(true);
                }}
                onDragLeave={() => setOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setOver(false);
                  const file = e.dataTransfer.files[0];
                  if (file) void readFile(file);
                }}
              >
                <FileJson size={30} aria-hidden />
                <strong>
                  {source?.name
                    ? `Choose a different file`
                    : "Drop a .json file here"}
                </strong>
                <span>or click to browse your computer</span>
                {source?.name && (
                  <span className="ps-dropzone-file">
                    {source.name} · {formatBytes(source.size)}
                  </span>
                )}
                <input
                  ref={fileInput}
                  type="file"
                  className="sr-only"
                  accept=".json,application/json,text/plain"
                  aria-label={`${title}: JSON file`}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (file) void readFile(file);
                  }}
                />
              </label>
            ) : (
              <div className="ps-import-paste">
                <JsonCodeEditor
                  label={`${title}: JSON text`}
                  value={text}
                  rows={12}
                  onChange={setText}
                />
                <div className="ps-actions">
                  <button
                    type="button"
                    className="button primary"
                    disabled={busy || !text.trim()}
                    onClick={() =>
                      check(text, { name: source?.name, size: text.length })
                    }
                  >
                    {busy ? "Checking…" : "Check JSON"}
                  </button>
                </div>
              </div>
            )}
            {busy && tab === "file" && (
              <p className="ps-muted" role="status">
                Checking {source?.name ?? "the file"}…
              </p>
            )}
            {syntax && tab === "file" && (
              <section
                className="ps-import-result"
                aria-labelledby={`${id}-syntax`}
              >
                <h3 id={`${id}-syntax`}>
                  <CircleAlert size={16} aria-hidden />{" "}
                  {source?.name ?? "This file"} isn't valid JSON yet
                </h3>
                <JsonProblem problem={syntax} />
                <div className="ps-actions">
                  <button
                    type="button"
                    className="button primary"
                    onClick={fix}
                  >
                    Fix it here
                  </button>
                  <button
                    type="button"
                    className="button"
                    onClick={() => fileInput.current?.click()}
                  >
                    Choose another file
                  </button>
                </div>
              </section>
            )}
            {problems && (
              <section
                className="ps-import-result"
                role="alert"
                aria-labelledby={`${id}-problems`}
              >
                <h3 id={`${id}-problems`}>
                  <CircleAlert size={16} aria-hidden />
                  {problems.length === 1
                    ? "This file can't be imported yet"
                    : `${problems.length} things to fix before importing`}
                </h3>
                <ValidationList problems={problems} />
                {text && (
                  <div className="ps-actions">
                    <button type="button" className="button" onClick={fix}>
                      Edit the JSON
                    </button>
                  </div>
                )}
              </section>
            )}
            {sample && (
              <p className="ps-import-sample">
                Not sure what the file should look like?{" "}
                <button
                  type="button"
                  className="text-button"
                  onClick={() => download(sample.fileName, sample.value)}
                >
                  <FileDown size={13} aria-hidden /> Download an example
                </button>
              </p>
            )}
          </>
        )}
        {step === "review" && plan && (
          <>
            <section
              className="ps-import-review"
              aria-label="What will be imported"
            >
              <h3>
                <CircleCheck size={16} aria-hidden />{" "}
                {plan.title ?? "Ready to import"}
              </h3>
              {source?.name && (
                <p className="ps-muted">
                  From {source.name} · {formatBytes(source.size)}
                </p>
              )}
              {!!plan.summary?.length && (
                <dl className="ps-facts">
                  {plan.summary.map((row) => (
                    <div key={row.label}>
                      <dt>{row.label}</dt>
                      <dd>{row.value}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {plan.changes && (
                <details
                  className="ps-change-details"
                  open={plan.changes.total <= 12}
                >
                  <summary>{describeSummary(plan.changes)}</summary>
                  <ChangeList summary={plan.changes} />
                </details>
              )}
              {!!plan.warnings?.length && (
                <div className="ps-notice" data-tone="warning">
                  <TriangleAlert size={16} aria-hidden />
                  <ul>
                    {plan.warnings.map((warning, i) => (
                      <li key={i}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
              {plan.replaces && (
                <div className="ps-notice" data-tone="danger">
                  <ShieldAlert size={16} aria-hidden />
                  <div>
                    <p>{plan.replaces}</p>
                    <label className="ps-check">
                      <input
                        type="checkbox"
                        checked={acknowledged}
                        onChange={(e) => setAcknowledged(e.target.checked)}
                      />
                      I understand
                    </label>
                  </div>
                </div>
              )}
            </section>
            {failure && (
              <p className="ps-notice" data-tone="danger" role="alert">
                <CircleAlert size={16} aria-hidden /> {failure}
              </p>
            )}
            <div className="ps-actions ps-actions-split">
              <button
                type="button"
                className="button"
                disabled={busy}
                onClick={() => setStep("choose")}
              >
                <ArrowLeft size={14} aria-hidden /> Back
              </button>
              <button
                type="button"
                className="button primary"
                disabled={busy || (!!plan.replaces && !acknowledged)}
                onClick={confirm}
              >
                {busy ? "Importing…" : (plan.confirmLabel ?? "Import")}
              </button>
            </div>
          </>
        )}
        {step === "done" && (
          <>
            <div className="ps-import-done">
              <CircleCheck size={28} aria-hidden />
              <p role="status">{undone ? "The import was undone." : done}</p>
            </div>
            <div className="ps-actions ps-actions-split">
              {onUndo && !undone ? (
                <button
                  type="button"
                  className="button"
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await onUndo();
                      setUndone(true);
                    } catch (error) {
                      setFailure(friendlyMessage(error));
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  <Undo2 size={14} aria-hidden /> Undo import
                </button>
              ) : (
                <span />
              )}
              <button
                type="button"
                className="button primary"
                onClick={onClose}
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

/**
 * A button that opens the guided importer. It keeps a real, labelled file
 * input so keyboard users, automation and tests can still choose a file
 * directly; a chosen file opens the importer at its check.
 */
export function JsonImportButton({
  label,
  fileLabel = label,
  kind = "",
  icon = <FileUp size={15} aria-hidden />,
  disabled = false,
  ...options
}: JsonImportOptions & {
  /** Visible button text. */
  label: string;
  /** Accessible name of the underlying file input (defaults to the label). */
  fileLabel?: string;
  kind?: string;
  icon?: ReactNode;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState<{ file?: File }>();
  return (
    <>
      <button
        type="button"
        className={`button ${kind}`}
        disabled={disabled}
        onClick={() => setOpen({})}
      >
        {icon}
        {label}
      </button>
      {/* Direct file choice for automation and assistive technology that
          prefers a file field; a chosen file opens the importer at its check. */}
      <input
        type="file"
        className="sr-only"
        tabIndex={-1}
        accept=".json,application/json"
        aria-label={fileLabel}
        disabled={disabled}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) setOpen({ file });
        }}
      />
      {open && (
        <JsonImportDialog
          {...options}
          initialFile={open.file}
          onClose={() => setOpen(undefined)}
        />
      )}
    </>
  );
}
